import {Box, Show, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import {useState} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {always} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useRenderCustomerMessageEmailTemplateMutation} from '@dms/api/core';
import i18n from '@dms/i18n';

import {Nullish, useDebouncedCallback, useDeepCompareEffect} from 'shared/web';

import {handleApiError} from '../../../utils/handleApiError';
import {FormValues} from '../types/SendOnlineOfferEmailFormSchema';
import {EmailWindow} from './EmailWindow';

interface EmailPreviewProps {
  businessCaseId: string;
  vehicleId: string;
  photoId: string | Nullish;
  sellerId: string;
  onlineOfferUri: string | Nullish;
  formApi: UseFormReturn<FormValues>;
}

const DUMMY_ONLINE_OFFER_URI = 'https://www.example.com/';

export function EmailPreview(props: EmailPreviewProps) {
  const [renderCustomerMessageTemplate, {isLoading: isLoadingEmail}] =
    useRenderCustomerMessageEmailTemplateMutation();

  const [htmlData, setHtmlData] = useState<string | null>(null);

  const data = props.formApi.watch(['emailAddress', 'contactPerson', 'body']);

  const isAnyDataMissing = match(data.map(isNilOrEmpty))
    .with(
      // when emailAddress is missing, we cannot fetch
      [true, Pattern.boolean, Pattern.boolean],
      // when contactPerson is missing, we cannot fetch
      [Pattern.boolean, true, Pattern.boolean],
      // when body is missing, we cannot fetch
      [Pattern.boolean, Pattern.boolean, true],
      always(true)
    )
    .otherwise(always(false));

  const handleRenderEmail = () => {
    const {emailAddress, contactPerson, body} = props.formApi.getValues();

    if (isNilOrEmpty(emailAddress) || isNilOrEmpty(contactPerson) || isNilOrEmpty(body)) {
      return;
    }

    renderCustomerMessageTemplate({
      customerMessageId: 'business_case-online_offer',
      renderCustomerMessageRequestBody: {
        customerId: contactPerson,
        customerMessageId: 'business_case-online_offer',
        payload: {
          businessCaseId: props.businessCaseId,
          vehicleId: props.vehicleId,
          photoId: props.photoId ?? null,
          sellerId: props.sellerId,
          body,
          onlineOfferUri: props.onlineOfferUri ?? DUMMY_ONLINE_OFFER_URI,
        },
      },
    })
      .unwrap()
      .then((data) => data.body)
      .then(setHtmlData)
      .catch(handleApiError);
  };

  const debouncedHandleRenderEmail = useDebouncedCallback(handleRenderEmail, 1000);

  useDeepCompareEffect(() => {
    if (isAnyDataMissing) {
      setHtmlData(null);
      return;
    }

    debouncedHandleRenderEmail();
  }, [data]);

  return (
    <VStack height="100%">
      <Box position="relative" height="100%">
        <Show when={isNilOrEmpty(htmlData)}>
          <EmailPreviewButtonWrapper data-testid="customerEmailForm-email-window-empty-preview">
            <VStack align="center">
              <Text size="xSmall" align="center" color="tertiary">
                {i18n.t('entity.customer.labels.fillAllRequiredFields')}
              </Text>
            </VStack>
          </EmailPreviewButtonWrapper>
        </Show>
        <EmailWindow
          htmlData={htmlData}
          isRerendering={isLoadingEmail && isNotNilOrEmpty(htmlData)}
          data-testid="customerEmailForm-email-window"
        />
      </Box>
    </VStack>
  );
}

const EmailPreviewButtonWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
